import React, { useState, useEffect, useRef, useCallback } from "react";
import { searchUsers, getUserSessionSummary, assignSessions } from "../services/adminUserSessionService";

const PLAN_TYPES = [
  { key: "GROUP",            label: "Group Sessions",            icon: "👥" },
  { key: "ONE_ON_ONE",       label: "One-on-One Sessions",       icon: "👤" },
  { key: "PARTNER_PRACTICE", label: "Partner Practice Sessions", icon: "🤝" },
];

const PLAN_KEY_MAP = {
  GROUP:            "group",
  ONE_ON_ONE:       "oneOnOne",
  PARTNER_PRACTICE: "partnerPractice",
};

const AdminAssignSessionsPage = () => {
  const [query, setQuery]               = useState("");
  const [options, setOptions]           = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching]       = useState(false);

  const [selectedUser, setSelectedUser]     = useState(null);
  const [summary, setSummary]               = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // per-row state: { GROUP: { value, saving, badge, error }, ... }
  const [rows, setRows] = useState({});

  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  // debounced search
  useEffect(() => {
    if (!query.trim()) { setOptions([]); setShowDropdown(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchUsers(query.trim());
        setOptions(data);
        setShowDropdown(true);
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [query]);

  // close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const initRows = useCallback((data) => {
    const init = {};
    PLAN_TYPES.forEach(({ key }) => {
      const planData = data[PLAN_KEY_MAP[key]];
      init[key] = {
        value:  planData?.totalSessions ?? 0,
        saving: false,
        badge:  planData ? "existing" : "new",
        error:  null,
      };
    });
    setRows(init);
  }, []);

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setQuery(`${user.name} — ${user.email}`);
    setShowDropdown(false);
    setLoadingSummary(true);
    setSummary(null);
    setRows({});
    try {
      const data = await getUserSessionSummary(user.id);
      setSummary(data);
      initRows(data);
    } catch {
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  const anySaving = Object.values(rows).some((r) => r.saving);

  const handleSave = async (planType) => {
    const val = rows[planType]?.value;
    if (val === "" || isNaN(val) || Number(val) < 0) {
      setRows((prev) => ({ ...prev, [planType]: { ...prev[planType], error: "Enter a valid non-negative number." } }));
      return;
    }
    setRows((prev) => ({ ...prev, [planType]: { ...prev[planType], saving: true, error: null } }));
    try {
      const updated = await assignSessions(selectedUser.id, planType, Number(val));
      setSummary(updated);
      const planData = updated[PLAN_KEY_MAP[planType]];
      setRows((prev) => ({
        ...prev,
        [planType]: { value: planData?.totalSessions ?? Number(val), saving: false, badge: "updated", error: null },
      }));
      setTimeout(() => {
        setRows((prev) => ({ ...prev, [planType]: { ...prev[planType], badge: "existing" } }));
      }, 2000);
    } catch {
      setRows((prev) => ({
        ...prev,
        [planType]: { ...prev[planType], saving: false, error: "Failed to save. Try again." },
      }));
    }
  };

  return (
    <div style={s.page}>
      <h2 style={s.heading}>Assign Sessions</h2>
      <p style={s.sub}>Search a user and assign or update their session counts.</p>

      {/* User Search */}
      <div style={s.searchWrap} ref={dropdownRef}>
        <label style={s.label}>Search User</label>
        <div style={{ position: "relative" }}>
          <input
            style={s.input}
            placeholder="Type name, email or mobile..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedUser(null);
              setSummary(null);
              setRows({});
            }}
            onFocus={() => options.length > 0 && setShowDropdown(true)}
          />
          {searching && <span style={s.searchSpinner}>⏳</span>}
        </div>

        {showDropdown && options.length > 0 && (
          <div style={s.dropdown}>
            {options.map((u) => (
              <div
                key={u.id}
                style={s.option}
                onClick={() => handleSelectUser(u)}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f9fa")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <span style={s.optName}>{u.name}</span>
                <span style={s.optMeta}>{u.email} · {u.mobileNumber}</span>
              </div>
            ))}
          </div>
        )}

        {showDropdown && !searching && options.length === 0 && query.trim() && (
          <div style={s.dropdown}>
            <div style={{ padding: "12px 16px", color: "#888", fontSize: "0.85rem" }}>No users found.</div>
          </div>
        )}
      </div>

      {/* Loading */}
      {loadingSummary && (
        <div style={s.loadingWrap}>
          <div style={s.spinner} />
          <span style={{ color: "#888", fontSize: "0.9rem" }}>Loading user sessions...</span>
        </div>
      )}

      {/* Session Form */}
      {summary && !loadingSummary && (
        <div style={s.formCard}>
          <div style={s.userInfo}>
            <span style={s.userName}>{summary.userName}</span>
            <span style={s.userEmail}>{summary.userEmail}</span>
          </div>

          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  <th style={s.th}>Plan Type</th>
                  <th style={s.th}>Current Total</th>
                  <th style={s.th}>Remaining</th>
                  <th style={s.th}>New Value</th>
                  <th style={s.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {PLAN_TYPES.map(({ key, label, icon }) => {
                  const planData = summary[PLAN_KEY_MAP[key]];
                  const row      = rows[key] || {};

                  return (
                    <tr key={key} style={s.tr}>
                      <td style={s.td}>
                        <div style={s.planCell}>
                          <span>{icon} {label}</span>
                          {row.badge === "new"      && <span style={{ ...s.badge, ...s.badgeNew }}>New</span>}
                          {row.badge === "existing" && <span style={{ ...s.badge, ...s.badgeExisting }}>Existing</span>}
                          {row.badge === "updated"  && <span style={{ ...s.badge, ...s.badgeUpdated }}>Updated ✓</span>}
                        </div>
                      </td>
                      <td style={s.td}>{planData?.totalSessions ?? 0}</td>
                      <td style={s.td}>{planData?.remainingSessions ?? 0}</td>
                      <td style={s.td}>
                        <input
                          type="number"
                          min={0}
                          style={s.numInput}
                          value={row.value ?? ""}
                          onChange={(e) =>
                            setRows((prev) => ({
                              ...prev,
                              [key]: { ...prev[key], value: e.target.value, error: null },
                            }))
                          }
                        />
                        {row.error && <div style={s.error}>{row.error}</div>}
                      </td>
                      <td style={s.td}>
                        <button
                          style={{
                            ...s.saveBtn,
                            opacity: anySaving ? 0.6 : 1,
                            cursor:  anySaving ? "not-allowed" : "pointer",
                          }}
                          disabled={anySaving}
                          onClick={() => handleSave(key)}
                        >
                          {row.saving ? "Saving..." : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  page:    { padding: "28px 24px", maxWidth: 860 },
  heading: { fontSize: "1.5rem", fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px" },
  sub:     { color: "#888", fontSize: "0.9rem", marginBottom: 28 },

  searchWrap:    { position: "relative", maxWidth: 480, marginBottom: 32 },
  label:         { display: "block", fontSize: "0.78rem", fontWeight: 600, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" },
  input:         { width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #ddd", fontSize: "0.95rem", outline: "none", boxSizing: "border-box" },
  searchSpinner: { position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: "0.85rem" },

  dropdown: { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff",
              border: "1px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              zIndex: 100, maxHeight: 240, overflowY: "auto" },
  option:   { padding: "10px 16px", cursor: "pointer", background: "#fff", transition: "background 0.15s" },
  optName:  { display: "block", fontWeight: 600, fontSize: "0.9rem", color: "#1a1a1a" },
  optMeta:  { display: "block", fontSize: "0.78rem", color: "#888", marginTop: 2 },

  loadingWrap: { display: "flex", alignItems: "center", gap: 12, padding: "24px 0" },
  spinner:     { width: 22, height: 22, border: "2px solid #e0e0e0", borderTop: "2px solid #028891",
                 borderRadius: "50%", animation: "spin 0.8s linear infinite" },

  formCard: { background: "#fff", borderRadius: 14, border: "1px solid #eee",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)", overflow: "hidden" },
  userInfo: { padding: "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12 },
  userName: { fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" },
  userEmail:{ fontSize: "0.85rem", color: "#888" },

  tableWrap: { overflowX: "auto" },
  table:     { width: "100%", borderCollapse: "collapse" },
  thead:     { background: "#f8f8f8" },
  th:        { padding: "11px 16px", fontSize: "0.75rem", fontWeight: 700, color: "#666",
               textAlign: "left", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" },
  tr:        { borderBottom: "1px solid #f0f0f0" },
  td:        { padding: "14px 16px", fontSize: "0.9rem", color: "#333", verticalAlign: "middle" },

  planCell:      { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  badge:         { fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 20 },
  badgeNew:      { background: "#e8f2ff", color: "#007BFF" },
  badgeExisting: { background: "#f0f0f0", color: "#666" },
  badgeUpdated:  { background: "#e6f4ea", color: "#2e7d32" },

  numInput: { width: 90, padding: "7px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: "0.9rem", outline: "none" },
  error:    { fontSize: "0.75rem", color: "#c62828", marginTop: 4 },

  saveBtn: { padding: "7px 20px", borderRadius: 6, border: "none", background: "#028891",
             color: "#fff", fontWeight: 600, fontSize: "0.85rem", transition: "opacity 0.15s" },
};

export default AdminAssignSessionsPage;
